import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import {
    gsap,
    ScrollTrigger,
    useGSAP,
    prefersReducedMotion
} from '@/lib/gsap'

interface MarqueeProps {
    children: ReactNode
    duration?: number
    reverse?: boolean
    repeat?: number
    className?: string
}

/**
 * Endless horizontal ticker that speeds up with the scroll.
 */
export const Marquee: FC<MarqueeProps> = ({
    children,
    duration = 30,
    reverse = false,
    repeat = 2,
    className
}): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        const track = trackRef.current

        if (!track || prefersReducedMotion()) return

        const loop = gsap.fromTo(track, { xPercent: reverse ? -50 : 0 }, {
            xPercent: reverse ? 0 : -50,
            duration,
            ease: 'none',
            repeat: -1
        })

        ScrollTrigger.create({
            trigger: rootRef.current,
            start: 'top bottom',
            end: 'bottom top',
            onUpdate: (self) => {
                const boost = 1 + Math.min(Math.abs(self.getVelocity()) / 450, 4)

                gsap.to(loop, {
                    timeScale: boost,
                    duration: 0.2,
                    overwrite: true,
                    onComplete: () => {
                        gsap.to(loop, { timeScale: 1, duration: 1, ease: 'power2.out' })
                    }
                })
            }
        })
    }, { scope: rootRef, dependencies: [duration, reverse] })

    const copy = Array.from({ length: repeat }, (_, index) => (
        <div key={index} className='flex shrink-0 items-center'>
            {children}
        </div>
    ))

    return (
        <div ref={rootRef} className={cn('overflow-hidden', className)}>
            <div ref={trackRef} className='flex w-max will-change-transform'>
                <div className='flex shrink-0 items-center'>
                    {copy}
                </div>

                <div className='flex shrink-0 items-center' aria-hidden='true' inert>
                    {copy}
                </div>
            </div>
        </div>
    )
}
